import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Card, Group, Modal, Box, Stack } from "@mantine/core";
import { useParams, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ErrorView, PageTitle, Subnav } from "@components/shared";
import {
  UserForm,
  UserAssessmentAssign,
  UserOrganizationManagement,
  UserOrgMembership,
} from "@components/User";
import { PasswordForm, PasskeyManager } from "@components/Auth";
import { AssetManagement } from "@components/Asset";
import { NoteManagement } from "@components/Note";
import { AssessmentList } from "@components/Assessment";

import useAppStore from "@duck/app-store";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function UserDetail({ isProfile }) {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [evaluationsRefresh, setEvaluationsRefresh] = useState(false);
  const params = useParams();
  const profileUserId = useAppStore((state) => state.user)?.id;
  const userId = isProfile ? profileUserId : params.id;

  useEffect(() => {
    if (!userId) return;
    fetchData();
  }, [userId]);

  function fetchData() {
    if (!userId) return;

    setError(null);
    setUser(null);

    axios
      .get(`/users/${userId}/`)
      .then(({ data }) => {
        setUser(data.response[0]);
      })
      .catch((err) => {
        setError(typeof err === "string" ? err : "Unable to load user");
        setUser(null);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Group mb="lg">
        <PageTitle
          logged_in={user.logged_in}
          title={`${user.first_name} ${user.last_name}`}
          subtitle={"User"}
          icon={entityIcon.user()}
          iconColor={entityColor.user}
        />
      </Group>
      {!isProfile && (
        <Box mt="md" mb="md">
          <Subnav
            links={[
              { text: t("detail"), to: `/users/${userId}` },
              {
                text: t("cs:organization_plural"),
                to: `/users/${userId}/organizations`,
              },
              {
                text: t("assessment_plural"),
                to: `/users/${userId}/evaluations`,
              },
              { text: t("note_plural"), to: `/users/${userId}/notes` },
              { text: t("document_plural"), to: `/users/${userId}/documents` },
            ]}
          />
        </Box>
      )}
      <Routes>
        <Route
          path="/"
          element={
            <Stack gap="md">
              <Card>
                <UserForm
                  id={userId}
                  email={user.email}
                  firstName={user.first_name}
                  lastName={user.last_name}
                  mfa={user.mfa}
                  onSuccess={fetchData}
                />
              </Card>
              {isProfile && <ChangePassword userId={userId} />}
              {isProfile && <PasskeyManager />}
            </Stack>
          }
        />
        <Route
          path="/organizations"
          element={
            <UserOrganizationManagement
              userId={userId}
              firstName={user.first_name}
            />
          }
        />
        <Route
          path="/organizations/:organizationId"
          element={<UserOrgMembershipRoute userId={userId} />}
        />
        <Route
          path="/evaluations"
          element={
            <>
              {!isProfile && (
                <UserAssessmentAssign
                  userId={userId}
                  firstName={user.first_name}
                  onAssigned={() => setEvaluationsRefresh((value) => !value)}
                />
              )}
              <AssessmentList
                userId={userId}
                refresh={evaluationsRefresh}
              />
            </>
          }
        />
        <Route
          path="/notes"
          element={<NoteManagement reqData={{ user_id: userId }} />}
        />
        <Route
          path="/documents"
          element={
            <AssetManagement
              reqData={{ user_id: userId }}
              title={t("document_plural")}
            />
          }
        />
      </Routes>
    </div>
  );
}

function UserOrgMembershipRoute({ userId }) {
  const { organizationId } = useParams();
  return (
    <UserOrgMembership userId={userId} organizationId={organizationId} />
  );
}

function ChangePassword({ userId }) {
  const [isOpen, setOpen] = useState(false);

  return (
    <React.Fragment>
      <Button
        variant="subtle"
        color="gray"
        size="xs"
        mt="lg"
        onClick={() => setOpen(true)}
      >
        Change Password
      </Button>
      <Modal
        opened={isOpen}
        onClose={() => setOpen(false)}
        title="Change Password"
      >
        <PasswordForm
          onSuccess={() => setOpen(false)}
          updating
          additionalReqData={{
            user_id: userId,
          }}
        />
      </Modal>
    </React.Fragment>
  );
}
